import type { ComplianceConsoleDefinition, ComplianceConsoleKind } from '../constants/complianceChecklists';
import { COMPLIANCE_CONSOLES } from '../constants/complianceChecklists';
import { COMPLIANCE_CATALOG_ES, type CatalogEsOverlay } from './complianceCatalogEs';

export type UiLanguage = 'en' | 'es';

const PROPER_NOUN = new RegExp(
  [
    'NextCompli',
    'Get Started',
    'Register Entity',
    'Unique Entity ID',
    'All Awards',
    'Check Entity Status',
    'Entity Under Review',
    'Pending ID Assignment',
    'Workspace',
    'Sign In',
    'Cookiebot',
    'CookieYes',
    'Termly',
    'iubenda',
    'Osano',
    'UserWay',
    'accessiBe',
    'PreVeil',
    'Entra',
    'Intune',
    'EDUCAUSE',
    'HECVAT',
    'FERPA',
    'GLBA',
    'CMMC',
    'DFARS',
    'NIST',
    'SPRS',
    'PIEE',
    'CAGE',
    'UEI',
    'EIN',
    'FCI',
    'CUI',
    'OFAC',
    'SDN',
    'CAPTA',
    'FTC',
    'GDPR',
    'CCPA',
    'CPRA',
    'WCAG',
    'DMCA',
    'PCI',
    'DSS',
    'COPPA',
    'ROSCA',
    'CAN-SPAM',
    'SPF',
    'DKIM',
    'DMARC',
    'BIMI',
    'Zero Trust',
    'CISA',
    'CIS',
    'ASVS',
    'MFA',
    'SSO',
    'WAF',
    'CSPM',
    'SBOM',
    'RFC',
    'ITAR',
    'EAR',
    'FOCI',
    'DCAA',
    'NAICS',
    'DSBS',
    'APEX',
    'OSBP',
    'GCC High',
    'Title IX',
    'Clery',
    'DoD',
    'IRS',
    'FinCEN',
    'BOI',
    'SBA',
    'SOS',
    'LLC',
    'FAR',
    'VPAT',
    'ACR',
    'CMP',
    'GPC',
    'SPI',
    'WISP',
    'MOU',
    'SOC 2',
    'DPA',
    'FSA',
  ].join('|'),
  'gi',
);

const stripComparable = (value: string): string => (
  value
    .replace(PROPER_NOUN, ' ')
    .replace(/https?:\/\/\S+/gi, ' ')
    .replace(/§\s*[\d.]+/g, ' ')
    .replace(/\b\d+(?:\.\d+)*\b/g, ' ')
    .replace(/[^a-zA-ZáéíóúüñÁÉÍÓÚÜÑ]+/g, ' ')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()
);

export const looksUntranslated = (english: string, spanish: string): boolean => {
  const left = stripComparable(english);
  const right = stripComparable(spanish);
  if (!right) {
    return true;
  }
  if (left.length < 8) {
    return false;
  }
  return left === right;
};

const requireField = (
  missing: string[],
  unchanged: string[],
  path: string,
  english: string | undefined,
  spanish: string | undefined,
  required: boolean,
): void => {
  if (!english) {
    return;
  }
  if (!spanish || !spanish.trim()) {
    if (required) {
      missing.push(path);
    }
    return;
  }
  if (looksUntranslated(english, spanish)) {
    unchanged.push(path);
  }
};

export const assertCatalogOverlay = (
  label: string,
  definition: ComplianceConsoleDefinition,
  overlay: CatalogEsOverlay | undefined,
): void => {
  const missing: string[] = [];
  const unchanged: string[] = [];
  if (!overlay) {
    throw new Error(`${label} has no Spanish overlay`);
  }
  requireField(missing, unchanged, `${label}.title`, definition.title, overlay.title, true);
  requireField(missing, unchanged, `${label}.subtitle`, definition.subtitle, overlay.subtitle, true);
  (definition.notices || []).forEach((notice) => {
    const row = overlay.notices[notice.id];
    if (!row) {
      missing.push(`${label}.notices.${notice.id}`);
      return;
    }
    requireField(missing, unchanged, `${label}.notices.${notice.id}.title`, notice.title, row.title, true);
    requireField(missing, unchanged, `${label}.notices.${notice.id}.body`, notice.body, row.body, true);
  });
  definition.pillars.forEach((pillar) => {
    const pillarEs = overlay.pillars[pillar.id];
    if (!pillarEs) {
      missing.push(`${label}.pillars.${pillar.id}`);
      return;
    }
    requireField(missing, unchanged, `${label}.pillars.${pillar.id}.label`, pillar.label, pillarEs.label, true);
    requireField(missing, unchanged, `${label}.pillars.${pillar.id}.summary`, pillar.summary, pillarEs.summary, true);
    pillar.items.forEach((item) => {
      const itemEs = pillarEs.items[item.id];
      if (!itemEs) {
        missing.push(`${label}.items.${item.id}`);
        return;
      }
      requireField(missing, unchanged, `${label}.items.${item.id}.title`, item.title, itemEs.title, true);
      requireField(missing, unchanged, `${label}.items.${item.id}.action`, item.action, itemEs.action, true);
      requireField(missing, unchanged, `${label}.items.${item.id}.evidenceHint`, item.evidenceHint, itemEs.evidenceHint, true);
      (item.actionSteps || []).forEach((step, index) => {
        requireField(
          missing,
          unchanged,
          `${label}.items.${item.id}.actionSteps.${index}`,
          step,
          itemEs.actionSteps?.[index],
          true,
        );
      });
      if ((item.actionSteps || []).length && (itemEs.actionSteps || []).length !== (item.actionSteps || []).length) {
        missing.push(`${label}.items.${item.id}.actionSteps`);
      }
      (item.waitingSteps || []).forEach((step, index) => {
        requireField(
          missing,
          unchanged,
          `${label}.items.${item.id}.waitingSteps.${index}`,
          step,
          itemEs.waitingSteps?.[index],
          true,
        );
      });
      if ((item.waitingSteps || []).length && (itemEs.waitingSteps || []).length !== (item.waitingSteps || []).length) {
        missing.push(`${label}.items.${item.id}.waitingSteps`);
      }
      (item.verifySteps || []).forEach((step) => {
        const stepEs = itemEs.verifySteps?.[step.id];
        requireField(
          missing,
          unchanged,
          `${label}.items.${item.id}.verifySteps.${step.id}.text`,
          step.text,
          stepEs?.text,
          true,
        );
        requireField(
          missing,
          unchanged,
          `${label}.items.${item.id}.verifySteps.${step.id}.evidenceHint`,
          step.evidenceHint,
          stepEs?.evidenceHint,
          true,
        );
      });
      if ((item.verifySteps || []).length && Object.keys(itemEs.verifySteps || {}).length !== (item.verifySteps || []).length) {
        missing.push(`${label}.items.${item.id}.verifySteps`);
      }
      requireField(missing, unchanged, `${label}.items.${item.id}.completeLabel`, item.completeLabel, itemEs.completeLabel, Boolean(item.completeLabel));
      requireField(missing, unchanged, `${label}.items.${item.id}.dependencyNote`, item.dependencyNote, itemEs.dependencyNote, Boolean(item.dependencyNote));
      requireField(missing, unchanged, `${label}.items.${item.id}.docsLabel`, item.docsLabel, itemEs.docsLabel, Boolean(item.docsLabel));
      requireField(missing, unchanged, `${label}.items.${item.id}.sourceLabel`, item.sourceLabel, itemEs.sourceLabel, false);
    });
  });
  if (missing.length || unchanged.length) {
    throw new Error(
      `${label} Spanish catalog is incomplete (missing: ${missing.join(', ') || 'none'}; untranslated/identical: ${unchanged.join(', ') || 'none'})`,
    );
  }
};

export const assertComplianceCatalogsLocalized = (): void => {
  (Object.keys(COMPLIANCE_CONSOLES) as ComplianceConsoleKind[]).forEach((kind) => {
    assertCatalogOverlay(kind, COMPLIANCE_CONSOLES[kind], COMPLIANCE_CATALOG_ES[kind]);
  });
};

export const englishCatalogTitles = (definition: ComplianceConsoleDefinition): string[] => (
  [
    definition.title,
    ...(definition.notices || []).map((notice) => notice.title),
    ...definition.pillars.flatMap((pillar) => [pillar.label, ...pillar.items.map((item) => item.title)]),
  ].filter((value) => value.trim().length > 12)
);

export const localizeComplianceConsole = (
  definition: ComplianceConsoleDefinition,
  language: UiLanguage,
): ComplianceConsoleDefinition => {
  if (language !== 'es') {
    return definition;
  }
  const overlay = COMPLIANCE_CATALOG_ES[definition.kind];
  if (!overlay) {
    return definition;
  }
  return {
    ...definition,
    title: overlay.title || definition.title,
    subtitle: overlay.subtitle || definition.subtitle,
    notices: (definition.notices || []).map((notice) => {
      const row = overlay.notices[notice.id];
      if (!row) {
        return notice;
      }
      return { ...notice, title: row.title, body: row.body };
    }),
    pillars: definition.pillars.map((pillar) => {
      const pillarEs = overlay.pillars[pillar.id];
      if (!pillarEs) {
        return pillar;
      }
      return {
        ...pillar,
        label: pillarEs.label,
        summary: pillarEs.summary,
        items: pillar.items.map((item) => {
          const itemEs = pillarEs.items[item.id];
          if (!itemEs) {
            return item;
          }
          return {
            ...item,
            title: itemEs.title,
            action: itemEs.action,
            actionSteps: itemEs.actionSteps || item.actionSteps,
            waitingSteps: itemEs.waitingSteps || item.waitingSteps,
            verifySteps: item.verifySteps?.map((step) => ({
              ...step,
              text: itemEs.verifySteps?.[step.id]?.text || step.text,
              evidenceHint: itemEs.verifySteps?.[step.id]?.evidenceHint || step.evidenceHint,
            })),
            evidenceHint: itemEs.evidenceHint,
            completeLabel: itemEs.completeLabel || item.completeLabel,
            dependencyNote: itemEs.dependencyNote || item.dependencyNote,
            docsLabel: itemEs.docsLabel || item.docsLabel,
            sourceLabel: itemEs.sourceLabel || item.sourceLabel,
          };
        }),
      };
    }),
  };
};
